'use client';

import Link from 'next/link';
import { useRouter } from 'next/navigation';

const FEATURED_EXPEDITIONS = [
  { name: 'Antarctica Fly-Cruise', location: 'Antarctica', nights: '8 nights, avoid the Drake', operator: 'Antarctica21', price: 'From $21,500 per guest', tags: ['Fly-Cruise', 'Antarctica', 'Penguins'] },
  { name: 'Greenland: Land of the Inuit', location: 'Greenland', nights: '12 nights, remote coast', operator: 'Ponant', price: 'From $15,800 per guest', tags: ['Greenland', 'Inuit Culture', 'Icebergs'] },
  { name: 'South Georgia, Falklands & Antarctica', location: 'Southern Ocean', nights: '20 nights, ultimate polar', operator: 'Quark Expeditions', price: 'From $32,900 per guest', tags: ['South Georgia', 'Falklands', 'Ultimate'] },
  { name: 'North Pole on 50 Years of Victory', location: 'Arctic Ocean', nights: '13 nights, atomic icebreaker', operator: 'Poseidon Expeditions', price: 'From $39,800 per guest', tags: ['North Pole', 'Atomic Icebreaker', 'Polar Bears'] },
];

const FEATURES = [
  { icon: '🧊', title: 'Polar-class vessels', desc: 'PC5 & PC6 rated ships built for genuine ice cruising.' },
  { icon: '✈', title: 'Fly-cruise options', desc: 'Skip the Drake Passage with air transfers to King George Island.' },
  { icon: '🔬', title: 'Onboard experts', desc: 'Naturalists, glaciologists, polar historians and photographers.' },
];

export default function PolarExpeditionsPage() {
  const router = useRouter();

  return (
    <div className="min-h-screen bg-[var(--background)] text-[var(--foreground)]">
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[var(--surface-2)]/95 backdrop-blur-xl border-b border-[var(--border)] h-16 flex items-center px-6">
        <Link href="/" className="text-[var(--primary)] font-bold tracking-widest uppercase mr-8" style={{ fontFamily: 'var(--font-serif)' }}>WorldwayLuxe</Link>
        <div className="flex items-center gap-6 text-sm text-[var(--muted-foreground)]">
          <Link href="/cruises" className="hover:text-[var(--primary)] transition-colors">Cruises</Link>
          <Link href="/safari" className="hover:text-[var(--primary)] transition-colors">Safari</Link>
          <Link href="/tailor-made" className="hover:text-[var(--primary)] transition-colors">Tailor-Made</Link>
        </div>
        <div className="ml-auto flex items-center gap-3">
          <Link href="/auth/login" className="text-sm text-[var(--muted-foreground)] hover:text-[var(--foreground)] px-4 py-2">Sign In</Link>
          <Link href="/auth/register" className="btn-primary text-sm px-5 py-2 rounded-lg">Join Now</Link>
        </div>
      </nav>
      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#050810] via-[var(--background)] to-[#0D1525]" />
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(ellipse 80% 60% at 50% 20%, rgba(201,168,76,0.15) 0%, transparent 70%)' }} />
        <div className="relative z-10 max-w-5xl mx-auto px-6">
          <p className="label-caps mb-3 text-[var(--primary)]">Ends of the Earth</p>
          <h1 className="text-4xl md:text-6xl font-bold mb-4" style={{ fontFamily: 'var(--font-serif)' }}>
            Polar Expeditions<br />
            <span className="gold-text-gradient">Where Few Have Stood.</span>
          </h1>
          <p className="text-lg text-[var(--muted-foreground)] mb-6 max-w-xl">
            Antarctica, the Arctic, Svalbard and Greenland aboard purpose-built expedition ships — polar bears, penguin colonies, icebergs and silence.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
            {FEATURES?.map((f) => (
              <div key={f?.title} className="glass-card rounded-xl p-4 border border-[rgba(201,168,76,0.1)]">
                <div className="text-2xl mb-2">{f?.icon}</div>
                <div className="text-sm font-bold mb-1">{f?.title}</div>
                <div className="text-xs text-[var(--muted-foreground)]">{f?.desc}</div>
              </div>
            ))}
          </div>
          <div className="flex gap-4">
            <button onClick={() => router?.push('/booking?vertical=polar')} className="btn-primary px-8 py-3 rounded-xl text-sm font-semibold">Design Your Expedition</button>
            <Link href="/ai-concierge" className="btn-ghost px-8 py-3 rounded-xl text-sm font-semibold border border-[var(--border)]">Ask Concierge</Link>
          </div>
        </div>
      </section>
      {/* Featured Expeditions */}
      <section className="py-16 border-t border-[var(--border)]">
        <div className="max-w-5xl mx-auto px-6">
          <p className="label-caps mb-2">Featured Expeditions</p>
          <h2 className="text-2xl font-bold mb-8" style={{ fontFamily: 'var(--font-serif)' }}>The World&apos;s Most <span className="gold-text-gradient">Remote Destinations</span></h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {FEATURED_EXPEDITIONS?.map((e) => (
              <div key={e?.name} className="glass-card rounded-xl p-5 hover:border-[rgba(201,168,76,0.3)] transition-all group cursor-pointer" onClick={() => router?.push('/booking?vertical=polar')}>
                <div className="text-2xl mb-3">🧊</div>
                <h3 className="text-base font-bold mb-1 group-hover:text-[var(--primary)] transition-colors" style={{ fontFamily: 'var(--font-serif)' }}>{e?.name}</h3>
                <p className="text-xs text-[var(--muted-foreground)] mb-1">{e?.location}</p>
                <p className="text-xs text-[var(--muted-foreground)] mb-1">{e?.nights}</p>
                <p className="text-xs text-[var(--muted-foreground)] mb-3">{e?.operator}</p>
                <div className="flex flex-wrap gap-1 mb-3">
                  {e?.tags?.map((tag) => (
                    <span key={tag} className="text-[10px] px-2 py-0.5 rounded-full bg-[rgba(201,168,76,0.1)] text-[var(--primary)]">{tag}</span>
                  ))}
                </div>
                <p className="text-xs font-semibold text-[var(--primary)]">{e?.price}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* FAQ */}
      <section className="py-16 border-t border-[var(--border)]">
        <div className="max-w-5xl mx-auto px-6">
          <p className="label-caps mb-6">FAQ</p>
          <div className="space-y-4">
            {[
              { q: 'What is the best time to visit?', a: 'November–March for Antarctica; May–September for the Arctic. The Antarctic summer offers 24-hour daylight and wildlife activity.' },
              { q: 'What is the Drake Passage?', a: 'The Drake Passage is the stretch of ocean between South America and Antarctica — notorious for rough seas. Fly-cruise options bypass it entirely.' },
              { q: 'What physical fitness is required?', a: 'Zodiac landings require stepping in and out of inflatable boats. Most guests in reasonable health can participate. We\'ll advise on specific requirements.' },
            ]?.map((faq) => (
              <div key={faq?.q} className="glass-card rounded-xl p-5">
                <div className="text-sm font-bold mb-2 text-[var(--primary)]">{faq?.q}</div>
                <div className="text-xs text-[var(--muted-foreground)] leading-relaxed">{faq?.a}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
      <footer className="border-t border-[var(--border)] py-8">
        <div className="max-w-5xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="text-xs text-[var(--muted-foreground)]">© 2026 Worldway Travels Group · All rights reserved.</div>
          <div className="flex items-center gap-6 text-xs text-[var(--muted-foreground)]">
            <Link href="/" className="hover:text-[var(--primary)] transition-colors">Home</Link>
            <Link href="/cruises" className="hover:text-[var(--primary)] transition-colors">Cruises</Link>
            <Link href="/safari" className="hover:text-[var(--primary)] transition-colors">Safari</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
